/**
 * Loyalty Connection (Sephora x Starbucks) — CMS content parsing (LC-145).
 *
 * The content-page-exp CMS response drives the page content. The typed items
 * (Banner, BannerList, Divider, …) live inside `data.contentSections[].content`.
 * The landing renders them with BESPOKE cards (not the generic building blocks)
 * so the layout matches the Figma design exactly — rounding, borders, shadows.
 * `getHeroContent` and `getContentSections` flatten the CMS shape into a small
 * render model the view consumes.
 *
 * Only Banner (hero), BannerList (card grids), and Divider drive the design;
 * any other/legacy section types in the response are ignored.
 *
 * Strings the CMS does NOT provide (CTA labels, linked-state copy, terms link)
 * live in the locale resource files (locales/LoyaltyConnection_{en_US,fr_CA}.js)
 * and are resolved in the components, so they are translated for fr-CA.
 */

// Base path for the content-page-exp CMS experience content, served via the
// same-origin /api/content proxy (see server apiRoutes.mjs `callCMSAPI`, route
// `/api/content/{*splat}`). The partner slug (the last path segment of the page
// URL, e.g. "starbucks") plus `loc`/`ch` are appended in the service, so this is
// generic across partner pages.
export const CONTENT_EXPERIENCE_PATH = '/api/content/experience';

export const CONTENT_CHANNEL = 'WEB';

/**
 * Extract the ordered building-block items from the content-page-exp response.
 *
 * The API returns `data.contentSections` as an array of content sections; the
 * typed building-block items (Banner, BannerList, Divider, SectionHeading,
 * Copy, …) live inside each section's `content` array. Flatten them into a
 * single ordered list for the landing view.
 *
 * @param {Object} cmsData - Raw API JSON ({ data: { contentSections: [{ content: [...] }] } }).
 * @returns {Array} Flat, ordered array of typed content items.
 */
export const getContentItems = cmsData =>
    (cmsData?.data?.contentSections || []).flatMap(section => (Array.isArray(section?.content) ? section.content : []));

// CMS BannerList sids for the two step sections, matched explicitly. Any other
// BannerList is treated as the perks grid (no sid dependency for perks).
const BANNER_LIST_SIDS = {
    howItWorks: 'Sephoraxstarbucks_how_it_works_bannerlist',
    howToEarn: 'Sephoraxstarbucks_HowtoEArn_bannerlist'
};

// Plain-text value of a Contentful rich-text node subtree.
const getNodeText = node => {
    if (!node) {
        return '';
    }

    if (typeof node.value === 'string') {
        return node.value;
    }

    return (node.content || []).map(getNodeText).join('');
};

// Split an item's rich text into a heading title + body description: the first
// heading node is the title; remaining non-empty blocks form the description.
const getItemTexts = item => {
    const blocks = item?.text?.json?.content || [];
    let title = '';
    const description = [];

    blocks.forEach(block => {
        const text = getNodeText(block).trim();

        if (!text) {
            return;
        }

        if (typeof block.nodeType === 'string' && block.nodeType.startsWith('heading')) {
            title = title || text;
        } else {
            description.push(text);
        }
    });

    return { title, description: description.join(' ') };
};

const getItemImage = item => item?.media?.src || item?.largeMedia?.src || '';

/**
 * Extract the hero from the CMS items: the leading Banner's image + heading.
 *
 * @param {Array} items - Flattened content items (from getContentItems).
 * @returns {Object|null} { image, mobileImage, backgroundColor, alt, heading }.
 */
export const getHeroContent = items => {
    const banner = items?.[0]?.type === 'Banner' ? items[0] : null;

    if (!banner) {
        return null;
    }

    return {
        image: banner.largeMedia?.src || banner.media?.src || '',
        mobileImage: banner.media?.src || banner.largeMedia?.src || '',
        backgroundColor: banner.backgroundColor || '',
        alt: banner.largeMedia?.altText || banner.media?.altText || '',
        heading: getItemTexts(banner).title
    };
};

/**
 * Classify the BannerLists into the sections the design uses. The two step lists
 * are matched on the CMS sid (BANNER_LIST_SIDS); any other BannerList defaults to
 * the perks grid:
 *   - howItWorks  "How It Works" (icon step cards; UNLINKED state)
 *   - howToEarn   "How Do I earn my 2x points…" (icon step cards; LINKED state)
 *   - perks       everything else (image cards; shown in all states)
 * The leading hero Banner, the CMS Divider, and any legacy section types are
 * ignored — the view supplies its own dividers to match the Figma layout.
 *
 * @param {Array} items - Flattened content items (from getContentItems).
 * @returns {Object} { perks?, howItWorks?, howToEarn? } each { title, cards }.
 */
export const getContentSections = items => {
    const sections = {};

    (items || []).forEach(item => {
        if (item?.type !== 'BannerList') {
            return;
        }

        const cards = (item.items || [])
            .filter(sub => sub?.media || sub?.largeMedia || sub?.text)
            .map(sub => ({ image: getItemImage(sub), ...getItemTexts(sub) }));

        const section = {
            title: item.title || '',
            cards
        };

        if (item.sid === BANNER_LIST_SIDS.howItWorks) {
            sections.howItWorks = section;
        } else if (item.sid === BANNER_LIST_SIDS.howToEarn) {
            sections.howToEarn = section;
        } else {
            // Anything that is not one of the two step lists is the perks grid.
            sections.perks = section;
        }
    });

    return sections;
};

// The partner slug is the last path segment of the current page URL
// (e.g. /starbucks or /ca/fr/starbucks -> "starbucks"). Browser-only — call at
// runtime (not during SSR render).
export const getPartnerSlug = () => {
    const segments = window.location.pathname.split('/').filter(Boolean);

    return segments[segments.length - 1] || '';
};

// True when the given partner slug appears in the account's linked partners
// (matched by partnerName, case-insensitive — same data as Account Settings).
export const isPartnerLinked = (linkedAccounts, slug) =>
    (linkedAccounts || []).some(account => account?.partnerName?.toLowerCase() === (slug || '').toLowerCase());

// Starbucks partner landing path — single source for the account-menu link href.
export const STARBUCKS_PATH = '/starbucks';

// Query param the partner OAuth flow (and a client-side link error) uses to signal
// the link result on the return URL, e.g. /starbucks?isSuccess=false.
export const LINK_RESULT_PARAM = 'isSuccess';

// The URL to return to when linking succeeds: the current partner page
// (/starbucks). The linked state is detected from the store on load.
// Browser-only — call at runtime (not during SSR render).
export const getLinkSuccessUrl = () => `${window.location.origin}${window.location.pathname}`;

// The URL to return to when linking fails: the current partner page with
// ?isSuccess=false, which the landing page detects to show the error popup.
// Browser-only — call at runtime (not during SSR render).
export const getLinkFailureUrl = () => `${getLinkSuccessUrl()}?${LINK_RESULT_PARAM}=false`;

// Linked-state shop links.
export const SHOP_ONLINE_URL = '/';

export const SHOP_MY_STORE_URL = '/happening/shop-my-store';

// Bottom-links destinations.
export const MANAGE_LINKED_ACCOUNTS_URL = '/profile/MyAccount';

export const POINTS_HISTORY_URL = '/profile/BeautyInsider/MyPoints';

export const TERMS_URL = '/terms-of-use';

// Where to send users who are not authorized to view the partner page.
export const UNAUTHORIZED_REDIRECT_URL = '/';

/**
 * Client-side port of loyalty-connection-service
 * `com.sephora.loyalty.connection.validate.RequestValidator#isAuthorizedLCUser`.
 *
 * Config mirrors that service's ConfigHub (`confighub.loyaltyconnection.*`) and
 * is read from the `Sephora.configurationSettings.loyaltyconnection` object,
 * using the exact same field names:
 *   loyaltyConnectionEnabled: boolean
 *   loyaltyConnectionStartDate: string             (ISO date-time)
 *   loyaltyConnectionBetaEnabled: boolean
 *   loyaltyConnectionBetaStartDate: string             (ISO date-time)
 *   loyaltyConnectionLaunchedPartnerNames: string      (comma-separated names)
 *   loyaltyConnectionBetaUserIds: string               (comma-separated ids)
 * The two comma-separated values are parsed into trimmed lists before matching.
 *
 * @param {string|number} loyaltyId - Profile id / clientId (user.profileId); the
 *   backend validates the beta-user list against this identifier.
 * @param {string} [partnerName] - Partner name/slug. When omitted (null/undefined),
 *   a non-empty launch list grants access.
 * @returns {boolean}
 */
export const isAuthorizedLCUser = (loyaltyId, partnerName) => {
    // Case 1: missing identifier — null, undefined, or a blank/empty string
    // (profileId can be transiently '' during load; treat that as "no id").
    if (loyaltyId == null || String(loyaltyId).trim() === '') {
        return false;
    }

    const cfg = Sephora?.configurationSettings?.loyaltyconnection || {};

    // Case 2: Loyalty Connection disabled
    if (!cfg.loyaltyConnectionEnabled) {
        return false;
    }

    const now = Date.now();
    const startDate = cfg.loyaltyConnectionStartDate ? new Date(cfg.loyaltyConnectionStartDate).getTime() : NaN;

    // Case 3: start date invalid (null or in the future)
    if (Number.isNaN(startDate) || startDate > now) {
        return false;
    }

    // The launch/beta config values are comma-separated strings — parse into
    // trimmed, non-empty lists (arrays accepted defensively too).
    const toList = value =>
        (typeof value === 'string' ? value.split(',') : Array.isArray(value) ? value : []).map(item => String(item).trim()).filter(Boolean);

    // Case 4: partner is in the launch list -> full access (names, case-insensitive).
    // With no specific partnerName (null/undefined), a non-empty launch list grants access.
    const launchedPartnerNames = toList(cfg.loyaltyConnectionLaunchedPartnerNames).map(name => name.toLowerCase());

    if (partnerName == null ? launchedPartnerNames.length > 0 : launchedPartnerNames.includes(String(partnerName).toLowerCase())) {
        return true;
    }

    // Case 5: partner not launched & beta disabled
    if (!cfg.loyaltyConnectionBetaEnabled) {
        return false;
    }

    const betaStartDate = cfg.loyaltyConnectionBetaStartDate ? new Date(cfg.loyaltyConnectionBetaStartDate).getTime() : NaN;

    // Case 6: beta start date invalid (null or in the future)
    if (Number.isNaN(betaStartDate) || betaStartDate > now) {
        return false;
    }

    // Case 7: user not in the beta list (ids compared as strings — loyaltyId /
    // Java Long can exceed JS safe-integer range).
    if (!toList(cfg.loyaltyConnectionBetaUserIds).includes(String(loyaltyId))) {
        return false;
    }

    // Case 8: beta user with valid beta conditions -> allow
    return true;
};
